import React from 'react';
import { Phone, Mail, Instagram, MapPin, Send } from 'lucide-react';
import { agencyInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${agencyInfo.whatsapp}?text=${encodeURIComponent('Olá! Gostaria de mais informações sobre os serviços.')}`, '_blank');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contato" className="bg-dark-primary border-t border-white/10">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <img 
              src={agencyInfo.logo} 
              alt={agencyInfo.name} 
              className="h-16 w-auto"
            />
            <p className="text-gray-400 leading-relaxed">
              {agencyInfo.tagline}
            </p>
            <div className="flex items-center space-x-4">
              <a 
                href={`https://instagram.com/${agencyInfo.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-gold rounded-full flex items-center justify-center text-white hover:text-dark-primary transition-all duration-300 hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={`https://wa.me/${agencyInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-gold rounded-full flex items-center justify-center text-white hover:text-dark-primary transition-all duration-300 hover:scale-110"
              >
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Links Rápidos</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('home')}
                  className="text-gray-400 hover:text-gold transition-colors duration-300"
                >
                  Início
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('portfolio')}
                  className="text-gray-400 hover:text-gold transition-colors duration-300"
                >
                  Serviços
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('sobre')}
                  className="text-gray-400 hover:text-gold transition-colors duration-300"
                >
                  Sobre
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contato')}
                  className="text-gray-400 hover:text-gold transition-colors duration-300"
                >
                  Contato
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Serviços</h4>
            <ul className="space-y-3">
              <li className="text-gray-400">Filmagem por Diária</li>
              <li className="text-gray-400">Gestão Presencial Completa</li>
              <li className="text-gray-400">Sites de Alta Conversão</li>
              <li className="text-gray-400">Captação com Drones FPV</li>
              <li className="text-gray-400">Cobertura de Eventos</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Contato</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${agencyInfo.phone}`}
                  className="flex items-start space-x-3 text-gray-400 hover:text-gold transition-colors duration-300 group"
                >
                  <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300" />
                  <span>{agencyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${agencyInfo.email}`}
                  className="flex items-start space-x-3 text-gray-400 hover:text-gold transition-colors duration-300 group"
                >
                  <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300" />
                  <span className="break-all">{agencyInfo.email}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`https://instagram.com/${agencyInfo.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-3 text-gray-400 hover:text-gold transition-colors duration-300 group"
                >
                  <Instagram className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300" />
                  <span>{agencyInfo.instagram}</span>
                </a>
              </li>
              <li className="flex items-start space-x-3 text-gray-400">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{agencyInfo.website}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 pt-12 border-t border-white/10">
          <div className="bg-gradient-to-r from-gold/10 to-transparent border border-gold/20 rounded-2xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Pronto para <span className="text-gold">Transformar</span> seu Negócio?
                </h3>
                <p className="text-gray-400">
                  Entre em contato e descubra como podemos ajudar você.
                </p>
              </div>
              <button
                onClick={handleWhatsApp}
                className="bg-gold hover:bg-gold-dark text-dark-primary font-bold px-8 py-4 rounded-lg transition-all duration-300 flex items-center space-x-2 hover:scale-105 hover:shadow-xl hover:shadow-gold/20 whitespace-nowrap"
              >
                <Send className="w-5 h-5" />
                <span>Agendar Diagnóstico</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
            <p>© {currentYear} {agencyInfo.name}. Todos os direitos reservados.</p>
            <p className="text-center md:text-right">
              Desenvolvido com <span className="text-gold">excelência</span> para resultados premium
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
